import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';
import './Lucro.css';

const PRESETS = [
  { id: '7d', label: 'Últimos 7 dias' },
  { id: 'this_month', label: 'Este mês' },
  { id: 'last_month', label: 'Mês passado' },
  { id: 'this_year', label: 'Este ano' },
  { id: 'all', label: 'Todo o tempo' }
];

/** Formata data em YYYY-MM-DD no fuso local (evita toISOString que usa UTC) */
function toLocalDateString(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getDateRange(preset) {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // hoje, meia-noite (fim do dia no backend é inclusive)
  let start;

  switch (preset) {
    case '7d': {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
      return { date_from: toLocalDateString(start), date_to: toLocalDateString(end) };
    }
    case 'this_month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { date_from: toLocalDateString(start), date_to: toLocalDateString(end) };
    case 'last_month': {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
      return { date_from: toLocalDateString(start), date_to: toLocalDateString(lastDay) };
    }
    case 'this_year':
      start = new Date(now.getFullYear(), 0, 1);
      return { date_from: toLocalDateString(start), date_to: toLocalDateString(end) };
    case 'all':
    default:
      return { date_from: '', date_to: '' };
  }
}

function getInitialDateRange() {
  return getDateRange('this_month');
}

function Lucro() {
  const [data, setData] = useState({ orders: [], resumo: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [datePreset, setDatePreset] = useState('this_month');
  const [dateFrom, setDateFrom] = useState(() => getInitialDateRange().date_from);
  const [dateTo, setDateTo] = useState(() => getInitialDateRange().date_to);
  const [metaMessage, setMetaMessage] = useState(null);
  const [metaStatus, setMetaStatus] = useState({ connected: false, ad_accounts: [], selected_ad_account_id: null });
  const [metaAccountSaving, setMetaAccountSaving] = useState(false);

  const fetchMetaStatus = useCallback(async () => {
    try {
      const res = await axios.get('/api/meta/status');
      if (res.data.success && res.data.connected) {
        setMetaStatus({
          connected: true,
          ad_accounts: res.data.ad_accounts || [],
          selected_ad_account_id: res.data.selected_ad_account_id || null
        });
      } else {
        setMetaStatus({ connected: false, ad_accounts: [], selected_ad_account_id: null });
      }
    } catch {
      setMetaStatus({ connected: false, ad_accounts: [], selected_ad_account_id: null });
    }
  }, []);

  useEffect(() => {
    fetchMetaStatus();
  }, [fetchMetaStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrlConnected = params.get('meta_connected') === '1';
    const fromUrlError = params.get('meta_error');
    const fromStorage = sessionStorage.getItem('meta_redirect') || sessionStorage.getItem('meta_show_success');

    if (fromUrlConnected || fromStorage === 'connected') {
      sessionStorage.removeItem('meta_show_success');
      setMetaMessage({ type: 'success', text: 'Meta Ads conectada. Selecione a conta de anúncios abaixo.' });
      (async () => {
        await fetchMetaStatus();
        setTimeout(() => fetchMetaStatus(), 1200);
        window.history.replaceState({}, '', window.location.pathname);
      })();
      fetchLucro(dateFrom, dateTo);
    } else if (fromUrlError || fromStorage === 'error') {
      sessionStorage.removeItem('meta_show_success');
      const msg = fromUrlError ? decodeURIComponent(fromUrlError) : 'Erro ao conectar Meta Ads.';
      setMetaMessage({ type: 'error', text: msg });
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount for URL params
  }, []);

  const fetchLucro = useCallback(async (from = dateFrom, to = dateTo) => {
    try {
      setLoading(true);
      setError('');
      const params = { limit: 2000 };
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const response = await axios.get('/api/lucro', { params });
      if (response.data.success && response.data.data) {
        setData(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar dados de lucro');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchLucro(dateFrom, dateTo);
  }, [dateFrom, dateTo, fetchLucro]);

  const handlePreset = (presetId) => {
    const { date_from, date_to } = getDateRange(presetId);
    setDatePreset(presetId);
    setDateFrom(date_from);
    setDateTo(date_to);
  };

  const handleDateFromChange = (e) => {
    setDateFrom(e.target.value || '');
    setDatePreset('custom');
  };

  const handleDateToChange = (e) => {
    setDateTo(e.target.value || '');
    setDatePreset('custom');
  };

  const handleMetaAccountChange = async (e) => {
    const adAccountId = e.target.value || null;
    setMetaAccountSaving(true);
    try {
      await axios.patch('/api/meta/account', { ad_account_id: adAccountId });
      setMetaStatus(prev => ({ ...prev, selected_ad_account_id: adAccountId }));
      await fetchLucro(dateFrom, dateTo);
    } catch (err) {
      setMetaMessage({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar conta' });
    } finally {
      setMetaAccountSaving(false);
    }
  };

  const formatBRL = (n) => {
    if (n == null || isNaN(n)) return '–';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(n);
  };

  const formatDate = (d) => {
    if (!d) return '–';
    const date = new Date(d);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const paymentLabel = (paymentType, installments) => {
    if (!paymentType && installments === 1) return '–';
    const t = String(paymentType || '').toLowerCase();
    if (t === 'pix') return 'PIX';
    if (installments === 1) return 'Créd. 1x';
    if (installments === 2) return 'Créd. 2x';
    if (installments >= 3) return `Créd. ${installments}x`;
    return paymentType || '–';
  };

  if (loading && !data.orders?.length) {
    return <div className="lucro-loading">⏳ Carregando dados de lucro...</div>;
  }

  if (error && !data.orders?.length) {
    return (
      <div className="lucro-error">
        ❌ {error}
        <button type="button" className="lucro-retry" onClick={() => fetchLucro()}>Tentar novamente</button>
      </div>
    );
  }

  const { orders = [], resumo = {} } = data;
  const apiBase = process.env.REACT_APP_API_URL || (window.location.port === '3000' ? `${window.location.protocol}//${window.location.hostname}:3001` : window.location.origin);
  const token = localStorage.getItem('token');
  const metaAuthUrl = token ? `${apiBase}/api/meta/auth?token=${encodeURIComponent(token)}` : '#';

  return (
    <div className="lucro-page">
      {metaMessage && (
        <div className={`lucro-meta-msg ${metaMessage.type === 'error' ? 'error' : 'success'}`}>
          {metaMessage.type === 'success' ? '✅' : '❌'} {metaMessage.text}
          <button type="button" className="lucro-meta-msg-close" onClick={() => setMetaMessage(null)}>×</button>
        </div>
      )}
      <div className="lucro-filters">
        <span className="lucro-filters-label">Período:</span>
        <div className="lucro-presets">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`lucro-preset-btn ${datePreset === p.id ? 'active' : ''}`}
              onClick={() => handlePreset(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="lucro-date-range">
          <label className="lucro-date-label">
            <span>Data início</span>
            <input
              type="date"
              value={dateFrom}
              onChange={handleDateFromChange}
              className="lucro-date-input"
            />
          </label>
          <label className="lucro-date-label">
            <span>Data fim</span>
            <input
              type="date"
              value={dateTo}
              onChange={handleDateToChange}
              className="lucro-date-input"
            />
          </label>
        </div>
        {loading && <span className="lucro-refreshing">Atualizando…</span>}
      </div>

      <div className="lucro-resumo">
        <h3>Resumo</h3>
        <div className="lucro-meta-connect">
          {metaStatus.connected ? (
            <div className="lucro-meta-account-row">
              <span className="lucro-meta-badge connected">Meta Ads conectada</span>
              {metaStatus.ad_accounts.length > 0 && (
                <label className="lucro-meta-select-wrap">
                  <span className="lucro-meta-select-label">Conta de anúncios:</span>
                  <select
                    value={metaStatus.selected_ad_account_id || ''}
                    onChange={handleMetaAccountChange}
                    disabled={metaAccountSaving}
                    className="lucro-meta-select"
                  >
                    <option value="">Selecione a conta</option>
                    {metaStatus.ad_accounts.map((acc) => (
                      <option key={acc.id} value={acc.id}>
                        {acc.name || acc.account_id || acc.id}
                      </option>
                    ))}
                  </select>
                  {metaAccountSaving && <span className="lucro-meta-saving">Salvando…</span>}
                </label>
              )}
            </div>
          ) : (
            <a href={metaAuthUrl} className="lucro-meta-btn">Conectar Meta Ads</a>
          )}
        </div>
        <div className="lucro-cards">
          <div className="lucro-card">
            <span className="lucro-card-label">Pedidos</span>
            <span className="lucro-card-value">{resumo.total_pedidos ?? orders.length}</span>
          </div>
          <div className="lucro-card lucro-card-receita">
            <span className="lucro-card-label">Receita</span>
            <span className="lucro-card-value">{formatBRL(resumo.total_receita)}</span>
          </div>
          <div className="lucro-card lucro-card-deducoes">
            <span className="lucro-card-label">Deduções</span>
            <span className="lucro-card-value">{formatBRL(resumo.total_deducoes)}</span>
          </div>
          <div className="lucro-card lucro-card-lucro">
            <span className="lucro-card-label">Lucro est.</span>
            <span className="lucro-card-value">{formatBRL(resumo.total_lucro_estimado)}</span>
          </div>
          <div className="lucro-card lucro-card-ads">
            <span className="lucro-card-label">Gastos com ads (Meta)</span>
            <span className="lucro-card-value">
              {resumo.meta_ads_error ? (
                <span className="lucro-ads-error" title={resumo.meta_ads_error}>{formatBRL(resumo.total_ads)}</span>
              ) : (
                formatBRL(resumo.total_ads)
              )}
            </span>
          </div>
          <div className="lucro-card lucro-card-ads-imposto">
            <span className="lucro-card-label">Imposto de ads (12%)</span>
            <span className="lucro-card-value">
              {formatBRL(resumo.total_ads_tax)}
            </span>
          </div>
          <div className="lucro-card lucro-card-ads-total">
            <span className="lucro-card-label">Ads + imposto</span>
            <span className="lucro-card-value">
              {formatBRL(resumo.total_ads_total)}
            </span>
          </div>
          <div className="lucro-card lucro-card-roas">
            <span className="lucro-card-label">ROAS</span>
            <span className="lucro-card-value">
              {resumo.total_ads_total != null && resumo.total_ads_total > 0 && resumo.total_receita != null
                ? `${(Number(resumo.total_receita) / Number(resumo.total_ads_total)).toFixed(2)}x`
                : '–'}
            </span>
            <small>receita / (ads + imposto)</small>
          </div>
          <div className="lucro-card lucro-card-custo-resultado">
            <span className="lucro-card-label">Custo por resultado</span>
            <span className="lucro-card-value">
              {resumo.total_pedidos != null && resumo.total_pedidos > 0 && resumo.total_ads_total != null
                ? formatBRL(Number(resumo.total_ads_total) / Number(resumo.total_pedidos))
                : '–'}
            </span>
            <small>(ads + imposto) / pedido</small>
          </div>
          <div className="lucro-card lucro-card-funcionaria">
            <span className="lucro-card-label">Funcionária (R$ 500/mês, dia 1)</span>
            <span className="lucro-card-value">{formatBRL(resumo.total_funcionaria)}</span>
            {resumo.meses_periodo != null && resumo.meses_periodo > 0 && (
              <small>{resumo.meses_periodo} {resumo.meses_periodo === 1 ? 'mês' : 'meses'}</small>
            )}
          </div>
          <div className={`lucro-card lucro-card-lucro-final${(resumo.lucro_final != null && resumo.lucro_final < 0) ? ' negative' : ''}`}>
            <span className="lucro-card-label">Lucro final</span>
            <span className="lucro-card-value">{formatBRL(resumo.lucro_final)}</span>
            <small>após ads e funcionária</small>
          </div>
        </div>
        <div className="lucro-resumo-detalhes">
          <span>Plataforma (2,5%): {formatBRL(resumo.total_taxa_plataforma)}</span>
          <span>Pagamento: {formatBRL(resumo.total_taxa_pagamento)}</span>
          <span>Custo produto (A4 R$30 / A3 R$60): {formatBRL(resumo.total_custo_produto)}</span>
          <span>Embalagem: {formatBRL(resumo.total_embalagem)}</span>
          <span>Mão de obra: {formatBRL(resumo.total_mao_de_obra)}</span>
          <span>Frete: {formatBRL(resumo.total_frete)}</span>
          <span>Gastos com ads (Meta): {formatBRL(resumo.total_ads)}</span>
          <span>Imposto estimado de ads (12%): {formatBRL(resumo.total_ads_tax)}</span>
          <span>Total ads + imposto: {formatBRL(resumo.total_ads_total)}</span>
          <span>ROAS (receita ÷ ads+imposto): {resumo.total_ads_total > 0 && resumo.total_receita != null ? `${(Number(resumo.total_receita) / Number(resumo.total_ads_total)).toFixed(2)}x` : '–'}</span>
          <span>Custo por resultado ((ads+imposto) ÷ pedido): {resumo.total_pedidos > 0 && resumo.total_ads_total != null ? formatBRL(Number(resumo.total_ads_total) / Number(resumo.total_pedidos)) : '–'}</span>
          <span>Funcionária (fixo R$ 500 no dia 1 de cada mês): {formatBRL(resumo.total_funcionaria)}</span>
        </div>
      </div>

      <div className="lucro-tabela-wrapper">
        <h3>Pedidos</h3>
        <div className="lucro-table-scroll">
          <table className="lucro-tabela">
            <thead>
              <tr>
                <th>Nº</th>
                <th>Data</th>
                <th>Cliente</th>
                <th>Pag.</th>
                <th className="lucro-num">Receita</th>
                <th className="lucro-num">Custo prod.</th>
                <th className="lucro-num">Taxa plat.</th>
                <th className="lucro-num">Taxa pag.</th>
                <th className="lucro-num">Embal.</th>
                <th className="lucro-num">Mão obra</th>
                <th className="lucro-num">Frete</th>
                <th className="lucro-num lucro-th-lucro">Lucro</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.shopify_order_id || order.id}>
                  <td className="lucro-td-numero">{order.order_number || order.shopify_order_id || '–'}</td>
                  <td className="lucro-td-data">{formatDate(order.created_at)}</td>
                  <td className="lucro-td-cliente">{order.customer_name || order.customer_email || '–'}</td>
                  <td>{paymentLabel(order.payment_type, order.installments)}</td>
                  <td className="lucro-num">{formatBRL(order.total_value)}</td>
                  <td className="lucro-num">{formatBRL(order.custo_produto)}</td>
                  <td className="lucro-num">{formatBRL(order.taxa_plataforma)}</td>
                  <td className="lucro-num">{formatBRL(order.taxa_pagamento)}</td>
                  <td className="lucro-num">{formatBRL(order.embalagem)}</td>
                  <td className="lucro-num">{formatBRL(order.mao_de_obra)}</td>
                  <td className="lucro-num">{formatBRL(order.frete)}</td>
                  <td className="lucro-num lucro-cell-lucro">{formatBRL(order.lucro_estimado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {orders.length === 0 && (
          <p className="lucro-empty">Nenhum pedido no período. Altere o período ou sincronize os pedidos (CartPanda).</p>
        )}
      </div>
    </div>
  );
}

export default Lucro;
